/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import Table from "components/Table";
import ContentShell from "components/ContentShell";
import { Box, IconButton, Menu, MenuItem } from "@mui/material";
import SearchInput from "components/SearchInput";
import usePaginationHook from "hooks/usePagination";
import { makeStyles } from "@mui/styles";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "hooks/useSnackbar";
import configData from "config/config.json";
import { getGamesList } from "services/gameService";
import sessionHelper from "services/sessionHelper";
import { PermittedRoles } from "utils/permissions";
import CustomButton from "components/CustomButton";
import useSearchHook from "hooks/useSearch";
import { defaultPageNo } from "utils/constants";
import { sortArray } from "utils/miscUtils";
import MoreVertIcon from "@mui/icons-material/MoreVert";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
  },
}));

const RowActions = ({ rowId, data, actionHandlers }: any) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget);
  };

  const handleRowAction = (data: any) => {
    setAnchorEl(null);
    navigate(configData.routes.masterGame.edit?.replace(":id", data?._id));
  };

  return (
    <>
      <IconButton onClick={handleClick}>
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        <MenuItem onClick={() => handleRowAction(data)}>Edit</MenuItem>
      </Menu>
    </>
  );
};

const MasterGameList = () => {
  const classes = useStyles();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [gamesList, setGamesList] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [orderDirection, setOrderDirection] = useState("asc");
  const { searchValue, onSearch: _onSearch } = useSearchHook();

  const hasCreatePermission = sessionHelper.hasRole(
    PermittedRoles.MASTER_GAME?.CREATE_ROLES
  );
  const hasEditPermission = sessionHelper.hasRole(
    PermittedRoles.MASTER_GAME?.EDIT_ROLES
  );

  const { onChangePage, onPageSizeChange, pageNo, pageSize } =
    usePaginationHook({ totalItems: totalCount });

  const columns = [
    {
      id: "_id",
      label: "ID",
      type: "string",
      isSortable: false,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "gameName",
      label: "Game Name",
      type: "string",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "gameAlias",
      label: "Game Alias",
      type: "string",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "isActiveText",
      label: "Active",
      type: "string",
      isSortable: true,
      alignHead: "left",
      alignBody: "left",
    },
    {
      id: "actions",
      label: "Actions",
      type: "string",
      isSortable: false,
      alignHead: "left",
      alignBody: "left",
    },
  ];

  const onColumnClick = (column: any) => {
    let _list = sortArray([...gamesList], orderDirection, column);
    setGamesList(_list);
    setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {
        pageNo,
        pageSize,
        ...(searchValue && { search: searchValue }),
      };

      const resp = await getGamesList(params);
      // Handle different possible response structures
      // Try data.platforms first (as per docs), then data.data, then data itself
      const games = resp?.data?.platforms || resp?.data?.data || resp?.data || [];

      // Transform data for table display
      const transformedGames = games.map((game) => ({
        ...game,
        isActiveText: game?.isActive ? "YES" : "NO",
        rowDisabled: !game?.isActive,
      }));

      setGamesList(transformedGames);
      setTotalCount(transformedGames.length || 0);
      setLoading(false);
    } catch (error) {
      showSnackbar(error.message, "error");
      setLoading(false);
    }
  };

  const onSearch = (value: any) => {
    _onSearch(value);
    onChangePage(defaultPageNo);
  };

  const handleCreate = () => {
    navigate(configData.routes.masterGame.create);
  };

  useEffect(() => {
    fetchData();
  }, [pageNo, pageSize, searchValue]);

  return (
    <ContentShell title="Master Game">
      <Box className={classes.container}>
        <Box className={classes.header}>
          <SearchInput value={searchValue} onSearch={onSearch} />
          {hasCreatePermission && (
            <CustomButton
              variant="contained"
              onClick={handleCreate}
              label="Create Game"
            />
          )}
        </Box>

        <Table
          columns={columns}
          data={gamesList}
          actions={RowActions}
          actionHandlers={{}}
          currentPage={pageNo}
          pageSize={pageSize}
          totalEntries={totalCount}
          onChangePage={onChangePage}
          onRowsPerPageChange={onPageSizeChange}
          onColumnClick={onColumnClick}
          highlightAltRow={true}
        />
      </Box>
    </ContentShell>
  );
};

export default MasterGameList;
